import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  newLocationRecordingOperationalNotificationPath,
  locationRecordingOperationalNotificationsPath,
  locationRequestPath,
  editLocationRequestProposalDraftPath,
  locationRequestProposalPublicationPath,
} from "@/routes";
import { Errors } from "../requests/controls";
import ProposalContent from "./content";
import type { Representation } from "./types";
export default function Preview({
  enquiry,
  proposal,
  draft_revision,
  sent_id,
  email_action_required,
  sent_revision,
  publishable,
  viewing_url,
  history,
  hold_hours,
}: {
  enquiry: { id: string; revision: string };
  proposal: { representation: Representation | null };
  draft_revision: string;
  sent_id: string | null;
  email_action_required: boolean;
  sent_revision: string | null;
  publishable: boolean;
  hold_hours: number;
  viewing_url: string | null;
  history: {
    id: string;
    representation: Representation;
    published_at: string;
    actor: string | null;
  }[];
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location.id;
  return (
    <AppLayout
      showWorkspaceNavigation={false}
      breadcrumbs={[
        {
          title: t("requests.title"),
          href: locationRequestPath(location, enquiry.id),
        },
      ]}
    >
      <Head title={t("proposals.preview")} />
      <PageCanvas>
        <p className="rounded-md border p-3 text-sm">
          {t("proposals.private_help")}
        </p>
        <Errors />
        {publishable && (
          <p className="text-muted-foreground text-sm">
            {t("proposals.publish_hold", { hours: hold_hours })}
          </p>
        )}
        {proposal.representation ? (
          <ProposalContent representation={proposal.representation} />
        ) : (
          <p role="alert">{t("proposals.recompose")}</p>
        )}
        <div className="flex flex-wrap gap-3">
          <Button
            variant="ghost"
            render={<Link href={locationRequestPath(location, enquiry.id)} />}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="outline"
            render={
              <Link
                href={editLocationRequestProposalDraftPath(
                  location,
                  enquiry.id,
                )}
              />
            }
          >
            {t("proposals.edit")}
          </Button>
          {publishable && (
            <Form
              action={locationRequestProposalPublicationPath(
                location,
                enquiry.id,
              )}
              method="post"
            >
              {({ processing }) => (
                <>
                  <input
                    type="hidden"
                    name="request_revision"
                    value={enquiry.revision}
                  />
                  <input
                    type="hidden"
                    name="draft_revision"
                    value={draft_revision}
                  />
                  <input
                    type="hidden"
                    name="sent_revision"
                    value={sent_revision || ""}
                  />
                  <Button type="submit" disabled={processing}>
                    {t("proposals.publish")}
                  </Button>
                </>
              )}
            </Form>
          )}
        </div>
        {email_action_required && (
          <p role="alert">{t("notifications.action_required")}</p>
        )}
        {sent_id && (
          <div className="flex flex-wrap gap-3">
            <Button
              render={
                <Link
                  href={newLocationRecordingOperationalNotificationPath(
                    location,
                    sent_id,
                  )}
                />
              }
            >
              {t("notifications.preview")}
            </Button>
            <Button
              variant="outline"
              render={
                <Link
                  href={locationRecordingOperationalNotificationsPath(
                    location,
                    sent_id,
                  )}
                />
              }
            >
              {t("notifications.history")}
            </Button>
          </div>
        )}
        {history.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">{t("proposals.history")}</h2>
            {history.map((version) => (
              <details key={version.id} className="rounded-lg border p-4">
                <summary className="cursor-pointer">
                  {version.actor} ·{" "}
                  {new Intl.DateTimeFormat(version.representation.locale, {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: version.representation.schedule.time_zone,
                  }).format(new Date(version.published_at))}
                </summary>
                <div className="mt-6">
                  <ProposalContent
                    representation={version.representation}
                    headingTag="h3"
                  />
                </div>
              </details>
            ))}
          </section>
        )}
        {viewing_url && (
          <section className="space-y-3 rounded-lg border p-4">
            <Label htmlFor="proposal-link">{t("proposals.link")}</Label>
            <Input
              id="proposal-link"
              readOnly
              value={viewing_url}
              onFocus={(event) => event.target.select()}
            />
            <p className="text-muted-foreground text-sm">
              {t("proposals.delivery_help")}
            </p>
            <a className="underline" href={viewing_url}>
              {t("proposals.open")}
            </a>
          </section>
        )}
      </PageCanvas>
    </AppLayout>
  );
}
